(() => {
  const toNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  document.querySelectorAll('[data-lm-product]').forEach((root) => {
    const mainImage = root.querySelector('.lm-gallery-main-image');
    const galleryMain = root.querySelector('.lm-gallery-main');
    const buildResponsiveSrcset = (url) => {
      if (!url) return '';
      try {
        const parsed = new URL(url, window.location.origin);
        return [480, 720, 960, 1200, 1400]
          .map((width) => {
            const candidate = new URL(parsed.href);
            candidate.searchParams.set('width', String(width));
            return `${candidate.toString()} ${width}w`;
          })
          .join(', ');
      } catch {
        return '';
      }
    };
    const caption = root.querySelector('[data-lm-gallery-caption]');
    const thumbnails = [...root.querySelectorAll('[data-lm-gallery-thumb]')];
    const galleryPrev = root.querySelector('[data-lm-gallery-prev]');
    const galleryNext = root.querySelector('[data-lm-gallery-next]');
    const galleryMainPrev = root.querySelector('[data-lm-gallery-main-prev]');
    const galleryMainNext = root.querySelector('[data-lm-gallery-main-next]');
    const galleryDots = [...root.querySelectorAll('[data-lm-gallery-dot]')];
    const variantCards = [...root.querySelectorAll('[data-lm-variant-card]')];
    const mainPrice = root.querySelector('[data-lm-main-price]');
    const quantityInput = root.querySelector('[data-lm-quantity]');
    const addButton = root.querySelector('.lm-add-to-cart');
    const normalizeQuantity = (value) => {
      const parsed = Number.parseInt(String(value ?? '').replace(',', '.'), 10);
      const minimum = Math.max(1, Number.parseInt(quantityInput?.min || '1', 10) || 1);
      return Number.isFinite(parsed) ? Math.max(minimum, parsed) : minimum;
    };
    const setQuantity = (value) => {
      if (!quantityInput) return;
      quantityInput.value = String(normalizeQuantity(value));
    };
    const calculator = root.querySelector('[data-lm-calculator]');
    const calculatorType = root.dataset.calculatorType || '';
    const unit = root.dataset.calculatorUnit || (calculatorType === 'plaster' ? 'kg' : 'l');
    const thicknessUnit = root.dataset.thicknessUnit || 'mm';
    const thicknessRate = toNumber(root.dataset.thicknessRate, 0);
    // Start collapsed; once the customer calculates, the result keeps it open.
    calculator?.removeAttribute('open');
    calculator?.setAttribute('aria-expanded', 'false');
    const alignCalculator = () => {
      if (!calculator) return;
      const desktop = window.matchMedia('(min-width: 861px)').matches;
      if (!desktop || calculator.open) {
        calculator.style.marginTop = '';
        return;
      }
      const collapsedGap = 2;
      calculator.style.marginTop = collapsedGap + 'px';
      const paymentIcons = root.querySelector('.lm-payment-icons');
      const fallbackCart = root.querySelector('.lm-add-to-cart');
      const alignTarget = paymentIcons || fallbackCart;
      if (!alignTarget) return;
      const calculatorRect = calculator.getBoundingClientRect();
      const targetRect = alignTarget.getBoundingClientRect();
      const delta = targetRect.bottom - calculatorRect.bottom;
      calculator.style.marginTop = Math.max(collapsedGap, collapsedGap + delta) + 'px';
    };
    alignCalculator();
    calculator?.addEventListener('toggle', alignCalculator);
    window.addEventListener('resize', alignCalculator);
    window.addEventListener('load', alignCalculator, { once: true });

    const similarScroller = root.querySelector('[data-lm-similar-scroller]');
    const similarList = root.querySelector('[data-lm-similar-list]');
    const similarPrev = root.querySelector('[data-lm-similar-prev]');
    const similarNext = root.querySelector('[data-lm-similar-next]');
    const updateSimilarNavigation = () => {
      if (!similarScroller || !similarPrev || !similarNext) return;
      const maxScroll = Math.max(0, similarScroller.scrollWidth - similarScroller.clientWidth);
      const hasOverflow = maxScroll > 2;
      const atStart = similarScroller.scrollLeft <= 2;
      const atEnd = similarScroller.scrollLeft >= maxScroll - 2;
      const previousDisabled = !hasOverflow || atStart;
      const nextDisabled = !hasOverflow || atEnd;
      similarPrev.disabled = previousDisabled;
      similarNext.disabled = nextDisabled;
      similarPrev.classList.toggle('pill-nav-item-disabled', previousDisabled);
      similarNext.classList.toggle('pill-nav-item-disabled', nextDisabled);
      similarPrev.setAttribute('aria-disabled', String(previousDisabled));
      similarNext.setAttribute('aria-disabled', String(nextDisabled));
    };
    const moveSimilar = (direction) => {
      if (!similarScroller) return;
      const firstCard = similarList?.querySelector('.grid-item');
      const gap = Number.parseFloat(window.getComputedStyle(similarList || similarScroller).columnGap) || 16;
      const step = firstCard
        ? firstCard.getBoundingClientRect().width + gap
        : similarScroller.clientWidth * 0.8;
      similarScroller.scrollBy({ left: direction * step, behavior: 'smooth' });
    };
    similarPrev?.addEventListener('click', () => moveSimilar(-1));
    similarNext?.addEventListener('click', () => moveSimilar(1));
    similarScroller?.addEventListener('scroll', updateSimilarNavigation, { passive: true });
    window.addEventListener('resize', updateSimilarNavigation);
    window.addEventListener('load', updateSimilarNavigation, { once: true });
    updateSimilarNavigation();
    window.requestAnimationFrame?.(updateSimilarNavigation);

    const activateGalleryImage = (thumbnail) => {
      if (!thumbnail) return;
      if (mainImage) {
        const imageUrl = thumbnail.dataset.imageUrl || mainImage.src;
        mainImage.src = imageUrl;
        const srcset = buildResponsiveSrcset(imageUrl);
        if (srcset) {
          mainImage.srcset = srcset;
        } else {
          mainImage.removeAttribute('srcset');
        }
        mainImage.alt = thumbnail.dataset.imageAlt || '';
      }
      const activeIndex = thumbnails.indexOf(thumbnail);
      thumbnails.forEach((item) => {
        const active = item === thumbnail;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      galleryDots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
      const captionText = thumbnail.dataset.caption || '';
      if (caption) {
        caption.textContent = captionText;
        caption.hidden = !captionText;
      }
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => activateGalleryImage(thumbnail));
    });

    const moveGallery = (direction) => {
      if (!thumbnails.length) return;
      const currentIndex = thumbnails.findIndex((item) => item.classList.contains('is-active'));
      const safeIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (safeIndex + direction + thumbnails.length) % thumbnails.length;
      activateGalleryImage(thumbnails[nextIndex]);
    };

    // Robust gallery switching: pointer swipe on the main image for touch devices.
    let swipeStart = null;
    galleryMain?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || event.isPrimary === false) return;
      swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      galleryMain.setPointerCapture?.(event.pointerId);
    }, { passive: true });
    galleryMain?.addEventListener('pointerup', (event) => {
      if (!swipeStart || event.pointerId !== swipeStart.id) return;
      const deltaX = event.clientX - swipeStart.x;
      const deltaY = event.clientY - swipeStart.y;
      swipeStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      moveGallery(deltaX < 0 ? 1 : -1);
    }, { passive: true });
    galleryMain?.addEventListener('pointercancel', () => {
      swipeStart = null;
    }, { passive: true });

    galleryPrev?.addEventListener('click', () => moveGallery(-1));
    galleryNext?.addEventListener('click', () => moveGallery(1));
    galleryMainPrev?.addEventListener('click', () => moveGallery(-1));
    galleryMainNext?.addEventListener('click', () => moveGallery(1));
    galleryDots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const requestedIndex = Number.parseInt(dot.dataset.galleryIndex || '', 10);
        const targetIndex = Number.isFinite(requestedIndex) ? requestedIndex : index;
        activateGalleryImage(thumbnails[targetIndex] || thumbnails[index]);
      });
    });

    const selectVariant = (card) => {
      if (!card || card.dataset.variantAvailable !== 'true') return;
      variantCards.forEach((item) => {
        const selected = item === card;
        item.classList.toggle('is-selected', selected);
        const radio = item.querySelector('[data-lm-variant-radio]');
        if (radio) radio.checked = selected;
      });
      if (mainPrice) mainPrice.textContent = card.dataset.variantPrice || '';
      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = 'In den Warenkorb';
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', card.dataset.variantId);
      window.history.replaceState({}, '', url);
    };

    variantCards.forEach((card) => card.addEventListener('click', () => selectVariant(card)));

    root.querySelector('[data-lm-quantity-minus]')?.addEventListener('click', () => {
      if (!quantityInput) return;
      setQuantity(normalizeQuantity(quantityInput.value) - 1);
    });

    root.querySelector('[data-lm-quantity-plus]')?.addEventListener('click', () => {
      if (!quantityInput) return;
      setQuantity(normalizeQuantity(quantityInput.value) + 1);
    });

    quantityInput?.addEventListener('change', () => setQuantity(quantityInput.value));
    quantityInput?.addEventListener('blur', () => setQuantity(quantityInput.value));
    const productForm = root.querySelector('#lm-product-form');
    productForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      setQuantity(quantityInput?.value || 1);

      const submitButton = productForm.querySelector('.lm-add-to-cart');
      if (!submitButton || submitButton.disabled) return;

      const defaultLabel = submitButton.textContent.trim();
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Wird hinzugefügt …';

      try {
        const response = await fetch(`${productForm.action}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(productForm)
        });
        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || 'Das Produkt konnte nicht hinzugefügt werden.');
        }

        const cartResponse = await fetch(`/cart.js?v=${Date.now()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json();
        const itemCount = Number(cart.item_count || 0);

        const sectionResponse = await fetch(
          `${window.location.pathname}?sections=header&v=${Date.now()}`,
          {
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          }
        );
        const sections = await sectionResponse.json();
        const headerMarkup = sections.header || '';
        const headerDocument = new DOMParser().parseFromString(headerMarkup, 'text/html');
        const freshCartLink = headerDocument.querySelector('.head-slot-cart-link-wrap');

        if (freshCartLink) {
          document.querySelectorAll('.head-slot-cart-link-wrap').forEach((cartLink) => {
            cartLink.innerHTML = freshCartLink.innerHTML;
          });
        }

        document.querySelectorAll('.head-slot-cart-link-quantity').forEach((badge) => {
          badge.textContent = String(itemCount);
          badge.hidden = itemCount === 0;
        });

        submitButton.textContent = 'Hinzugefügt';
        window.setTimeout(() => {
          submitButton.textContent = defaultLabel;
        }, 1800);
      } catch (error) {
        submitButton.textContent = 'Erneut versuchen';
        window.setTimeout(() => {
          submitButton.textContent = defaultLabel;
        }, 2500);
      } finally {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
    });

    root.querySelector('[data-lm-calculate]')?.addEventListener('click', () => {
      const result = root.querySelector('[data-lm-calculator-result]');
      const length = toNumber(root.querySelector('[data-lm-length]')?.value);
      const area = toNumber(root.querySelector('[data-lm-area]')?.value);
      const coats = calculatorType === 'paint' ? toNumber(root.querySelector('[data-lm-coats]')?.value, 1) : 1;
      const measure = calculatorType === 'length' ? length : area;
      if (!result) return;
      calculator?.setAttribute('open', '');
      calculator?.setAttribute('aria-expanded', 'true');
      result.replaceChildren();

      let needed = 0;
      let resultUnit = unit;
      let inputValid = measure > 0;

      if (calculatorType === 'plaster') {
        const thicknessInput = root.querySelector('[data-lm-thickness]');
        const thickness = toNumber(thicknessInput?.value);
        const thicknessRows = [...root.querySelectorAll('[data-lm-thickness-row]')]
          .map((row) => ({
            mm: toNumber(row.dataset.mm),
            kgPerM2: toNumber(row.dataset.kgPerM2)
          }))
          .filter((row) => row.mm > 0 && row.kgPerM2 > 0)
          .sort((a, b) => a.mm - b.mm);
        const minThickness = toNumber(thicknessInput?.min, thicknessRows[0]?.mm || 0);
        const maxThickness = toNumber(thicknessInput?.max, thicknessRows[thicknessRows.length - 1]?.mm || 0);
        let kgPerM2 = 0;
        if (thicknessRate > 0 && thickness >= minThickness && thickness <= maxThickness) {
          kgPerM2 = thickness * thicknessRate;
        } else if (thicknessRows.length && thickness >= minThickness && thickness <= maxThickness) {
          const upperIndex = thicknessRows.findIndex((row) => row.mm >= thickness);
          if (upperIndex <= 0) {
            kgPerM2 = thicknessRows[0].kgPerM2;
          } else if (upperIndex >= thicknessRows.length) {
            kgPerM2 = thicknessRows[thicknessRows.length - 1].kgPerM2;
          } else {
            const lower = thicknessRows[upperIndex - 1];
            const upper = thicknessRows[upperIndex];
            const span = upper.mm - lower.mm;
            const ratio = span > 0 ? (thickness - lower.mm) / span : 0;
            kgPerM2 = lower.kgPerM2 + (upper.kgPerM2 - lower.kgPerM2) * ratio;
          }
        }
        needed = area * kgPerM2;
        inputValid = area > 0 && thickness >= minThickness && thickness <= maxThickness && kgPerM2 > 0;
        if (!inputValid && result) {
          const error = document.createElement('p');
          error.className = 'lm-calculator-error';
          error.textContent = 'Bitte Fläche und eine Auftragsstärke zwischen ' + minThickness.toLocaleString('de-DE') + ' und ' + maxThickness.toLocaleString('de-DE') + ' ' + thicknessUnit + ' eingeben.';
          result.append(error);
          return;
        }
        if (inputValid && result) {
          const thicknessNote = document.createElement('p');
          thicknessNote.className = 'lm-calculator-error';
          thicknessNote.style.color = 'inherit';
          thicknessNote.textContent = 'Auftragsstärke: ' + thickness.toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' ' + thicknessUnit;
          result.append(thicknessNote);
        }
      } else {
        const consumption = toNumber(root.dataset.consumption, 0);
        needed = calculatorType === 'length' ? measure : measure * (calculatorType === 'paint' ? coats : 1) * consumption;
        inputValid = measure > 0 && (calculatorType !== 'paint' || coats > 0) && consumption > 0;
      }

      if (!inputValid) {
        const error = document.createElement('p');
        error.className = 'lm-calculator-error';
        error.textContent = calculatorType === 'plaster'
          ? 'Bitte Fläche und Auftragsstärke vollständig eingeben.'
          : calculatorType === 'length'
            ? 'Bitte eine Länge größer als 0 lfm eingeben.'
            : calculatorType === 'area'
              ? 'Bitte eine Fläche größer als 0 m² eingeben.'
              : 'Bitte Fläche und Anzahl der Anstriche vollständig eingeben.';
        result.append(error);
        return;
      }

      const heading = document.createElement('p');
      heading.className = 'lm-calculator-error';
      heading.style.color = 'inherit';
      heading.textContent = (root.dataset.calculatorLabel || 'Benötigte Menge') + ': ca. ' + needed.toLocaleString('de-DE', { maximumFractionDigits: 2 }) + ' ' + resultUnit;
      result.append(heading);

      variantCards
        .filter((card) => toNumber(card.dataset.packageSize) > 0)
        .sort((a, b) => toNumber(b.dataset.packageSize) - toNumber(a.dataset.packageSize))
        .forEach((card) => {
          const packageSize = toNumber(card.dataset.packageSize);
          const count = Math.ceil(needed / packageSize);
          const option = document.createElement('div');
          option.className = 'lm-result-option';

          const title = document.createElement('strong');
          title.textContent = card.querySelector('strong')?.textContent || (packageSize + ' ' + (card.dataset.packageUnit || unit));

          const amount = document.createElement('span');
          amount.textContent = count + ' × ' + (card.querySelector('strong')?.textContent || (packageSize + ' ' + (card.dataset.packageUnit || unit)));

          const choose = document.createElement('button');
          choose.type = 'button';
          choose.textContent = card.dataset.variantAvailable === 'true' ? 'Variante übernehmen' : 'Derzeit nicht verfügbar';
          choose.disabled = card.dataset.variantAvailable !== 'true';
          choose.addEventListener('click', () => {
            selectVariant(card);
            setQuantity(count);
            calculator?.setAttribute('open', '');
          });

          option.append(title, amount, choose);
          result.append(option);
        });
    });
  });
})();
